import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { createHabit } from '../store/slices/habitsSlice';
import { fetchWeeklyView } from '../store/slices/recordsSlice';
import { closeAddHabitModal } from '../store/slices/uiSlice';
import { useTranslation } from '../hooks/useTranslation';
import '../styles/Modal.css';

const AddHabitModal: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAddHabitModalOpen } = useSelector((state: RootState) => state.ui);
  const { t, language } = useTranslation();
  
  const [habitData, setHabitData] = useState({
    name: '',
    description: '',
    color: '#4CAF50',
    icon: '📝',
    repeatDays: {
      mon: true, tue: true, wed: true, thu: true,
      fri: true, sat: true, sun: true
    },
    goalType: 'continuous' as 'continuous' | 'weekly',
    weeklyGoal: 3
  });

  const colors = [
    '#4CAF50', '#2196F3', '#FF9800', '#E91E63',
    '#9C27B0', '#00BCD4', '#FFC107', '#795548'
  ];

  const emojis = [
    '📝', '💪', '📚', '🏃', '💧', '🧘', '💊', '🎯',
    '🎨', '🎸', '💻', '📖', '✍️', '🏋️', '🚶', '🍎'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!habitData.name.trim()) return;

    const habitToCreate = {
      name: habitData.name,
      description: habitData.description,
      color: habitData.color,
      icon: habitData.icon,
      isArchived: 0,
      reminderEnabled: 0,
      reminderTime: '09:00',
      repeatDays: habitData.repeatDays,
      goalType: habitData.goalType,
      weeklyGoal: habitData.goalType === 'weekly' ? habitData.weeklyGoal : undefined
    };
    
    try {
      const result = await dispatch(createHabit(habitToCreate));
      
      if (result.type.endsWith('/fulfilled')) {
        console.log('[AddHabitModal] Habit created successfully');
        console.log('[AddHabitModal] Refreshing weekly view...');
        dispatch(fetchWeeklyView());
        
        // フォームをリセット
        setHabitData({
          name: '',
          description: '',
          color: '#4CAF50',
          icon: '📝',
          repeatDays: {
            mon: true, tue: true, wed: true, thu: true,
            fri: true, sat: true, sun: true
          },
          goalType: 'continuous',
          weeklyGoal: 3
        });
        dispatch(closeAddHabitModal());
      } else if (result.type.endsWith('/rejected')) {
        console.error('[AddHabitModal] Habit creation failed:', result);
        alert(t('habit.createFailed'));
      }
    } catch (error) {
      console.error('[AddHabitModal] Error creating habit:', error);
      alert(t('common.error'));
    }
  };

  if (!isAddHabitModalOpen) return null;

  return (
    <div className="modal-overlay" onClick={() => dispatch(closeAddHabitModal())}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('habit.add')}</h2>
          <button className="modal-close" onClick={() => dispatch(closeAddHabitModal())}>×</button>
        </div>
        
        <form className="habit-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="habit-name">📝 {t('habit.name')}</label>
            <input
              id="habit-name"
              type="text"
              value={habitData.name}
              onChange={(e) => setHabitData({...habitData, name: e.target.value})}
              placeholder={t('habit.namePlaceholder')}
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="habit-description">📄 {t('habit.description')}</label>
            <textarea
              id="habit-description"
              value={habitData.description}
              onChange={(e) => setHabitData({...habitData, description: e.target.value})}
              placeholder={t('habit.descriptionPlaceholder')}
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>🎨 {t('habit.color')}</label>
            <div className="color-selector">
              {colors.map(color => (
                <button
                  key={color}
                  type="button"
                  className={`color-option ${habitData.color === color ? 'selected' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setHabitData({...habitData, color})}
                />
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>😊 {t('habit.icon')}</label>
            <div className="emoji-selector">
              {emojis.map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  className={`emoji-option ${habitData.icon === emoji ? 'selected' : ''}`}
                  onClick={() => setHabitData({...habitData, icon: emoji})}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>📅 {t('habit.repeatDays')}</label>
            <div className="weekday-selector">
              {Object.entries(habitData.repeatDays).map(([day, checked]) => (
                <label key={day} className={`weekday-option ${checked ? 'checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => setHabitData({
                      ...habitData,
                      repeatDays: {
                        ...habitData.repeatDays,
                        [day]: e.target.checked
                      }
                    })}
                  />
                  <span>{t(`weekday.${day}`)}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="goal-type">🎯 {t('habit.goalType')}</label>
            <select
              id="goal-type"
              value={habitData.goalType}
              onChange={(e) => setHabitData({
                ...habitData,
                goalType: e.target.value as 'continuous' | 'weekly'
              })}
            >
              <option value="continuous">{t('habit.continuousGoal')}</option>
              <option value="weekly">{t('habit.weeklyGoal')}</option>
            </select>
          </div>

          {habitData.goalType === 'weekly' && (
            <div className="form-group">
              <label htmlFor="weekly-goal">{t('habit.weeklyGoalCount')}</label>
              <div className="weekday-goal-input">
                <select
                  id="weekly-goal"
                  value={habitData.weeklyGoal}
                  onChange={(e) => setHabitData({
                    ...habitData,
                    weeklyGoal: parseInt(e.target.value) || 1
                  })}
                >
                  {[1,2,3,4,5,6,7].map((n) => (
                    <option key={n} value={n}>{`${n} ${t('habit.timesPerWeek')}`}</option>
                  ))}
                </select>
              </div>
              <div className="goal-help" style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginTop: '5px',
                lineHeight: '1.4'
              }}>
                {t('habit.weeklyGoalExample').replace('{count}', habitData.weeklyGoal.toString())}
              </div>
            </div>
          )}
          
          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => dispatch(closeAddHabitModal())}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
            >
              {t('habit.add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddHabitModal;
